/**
 * „Dodaj do Kalendarza Apple" in Integracje → Kalendarz (owner, 28 October 2026): a text-only button
 * on a Mac, iPhone or iPad that opens Calendar's own subscribe dialog; with the feed on, „Wyłącz
 * link" and „Utwórz nowy" beside it. The address is never drawn (calendar-sync R10). No Apple mark:
 * Apple does not license its icons for this.
 */

import { useTranslation } from 'react-i18next';

import { instantWhen } from '~/shared/lib/instantWhen';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { FormBanner } from '~/shared/ui/FormBanner';
import ghost from '~/shared/ui/ghostButton.module.css';
import ink from '~/shared/ui/inkButton.module.css';

import type { AppleCalendarApi } from '../lib/useAppleCalendar';
import screen from './AccountScreen.module.css';
import styles from './CalendarTab.module.css';

const KEY = 'web:account.calendar.apple';
const INK = `${String(ink.ink)} ${String(screen.accountButton)} ${String(screen.accountButtonBold)}`;
const GHOST = `${String(ghost.ghost)} ${String(screen.accountButton)} ${String(screen.canvasGhost)}`;

/** `apple` is the screen's `useAppleCalendar`: the section heading's „pick one" note reads it too. */
export function AppleCalendarBlock({
  apple,
  className,
}: {
  readonly apple: AppleCalendarApi;
  readonly className: string;
}) {
  const { t, i18n } = useTranslation();
  const { view } = apple;
  if (view.kind === 'hidden') return null;

  return (
    <div className={`${String(screen.block)} ${className}`}>
      <h3 className={screen.blockTitle}>{t(`${KEY}.title`)}</h3>
      {/* The server only sees the feed being fetched, so „connected" means a calendar has fetched it. */}
      {view.enabled ? (
        <>
          <b className={screen.rowTitle}>{t(view.lastFetchedAt === null ? `${KEY}.linkOn` : `${KEY}.connected`)}</b>
          <span className={screen.rowNote}>
            {view.lastFetchedAt === null
              ? t(`${KEY}.fetchPending`)
              : t(`${KEY}.lastFetched`, {
                  when: instantWhen(Date.parse(view.lastFetchedAt), apple.answeredAt, i18n.language),
                })}
          </span>
        </>
      ) : null}
      <span className={screen.rowNote}>{t(`${KEY}.note`)}</span>
      <div className={styles.actions}>
        <button
          type="button"
          className={INK}
          aria-disabled={apple.busy ? 'true' : undefined}
          aria-busy={apple.busy}
          onClick={apple.onSubscribe}
        >
          {t(`${KEY}.add`)}
        </button>
        {view.enabled ? (
          <>
            <button
              type="button"
              className={GHOST}
              aria-disabled={apple.busy ? 'true' : undefined}
              onClick={() => {
                apple.onAsk('rotate');
              }}
            >
              {t(`${KEY}.rotate`)}
            </button>
            <button
              type="button"
              className={GHOST}
              aria-disabled={apple.busy ? 'true' : undefined}
              onClick={() => {
                apple.onAsk('disable');
              }}
            >
              {t(`${KEY}.disable`)}
            </button>
          </>
        ) : null}
      </div>
      <FormBanner message={apple.refusal} />
      <ConfirmDialog prompt={apple.prompt} onConfirm={apple.onConfirm} onCancel={apple.onCancel} />
    </div>
  );
}
